import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  historySchema,
  incidentSchema,
  overviewSchema,
  previewSchema,
  ruleSchema,
  rulesOverviewSchema,
} from './contracts';
import type {
  AlertOverview,
  AlertRule,
  Incident,
  IncidentFilters,
  IncidentHistory,
  RuleDefinition,
  RulePreview,
  RulesOverview,
  SaveRule,
  SensorScope,
  UpdateIncident,
} from './contracts';
import { callAlerts } from './transport';

export interface UnifiedAlertsService {
  rules: (cursor: string | null) => Promise<RulesOverview>;
  saveRule: (command: SaveRule) => Promise<AlertRule>;
  previewRule: (rule: RuleDefinition, scope: SensorScope) => Promise<RulePreview>;
  incidents: (filters: IncidentFilters, cursor: string | null) => Promise<AlertOverview>;
  history: (incidentId: string, cursor: string | null) => Promise<IncidentHistory>;
  updateIncident: (command: UpdateIncident) => Promise<{ incident: Incident }>;
}

export const unifiedAlertsService: UnifiedAlertsService = {
  rules: (cursor) => callAlerts('GET_UNIFIED_ALERT_RULES', { cursor }, rulesOverviewSchema),
  saveRule: (payload) => callAlerts('SAVE_UNIFIED_ALERT_RULE', payload, ruleSchema),
  previewRule: (rule, scope) =>
    callAlerts('PREVIEW_UNIFIED_ALERT_RULE', { rule, ...scope }, previewSchema),
  incidents: (filters, cursor) =>
    callAlerts('GET_UNIFIED_ALERT_OVERVIEW', { ...filters, cursor }, overviewSchema),
  history: (incidentId, cursor) =>
    callAlerts('GET_UNIFIED_INCIDENT_HISTORY', { incidentId, cursor }, historySchema),
  updateIncident: (payload) =>
    callAlerts('UPDATE_UNIFIED_INCIDENT', payload, z.object({ incident: incidentSchema })),
};

function uniqueById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

export function useUnifiedIncidents(
  filters: IncidentFilters,
  service = unifiedAlertsService,
  enabled = true
) {
  const query = useInfiniteQuery({
    queryKey: ['unified-alerts', 'incidents', filters],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => service.incidents(filters, pageParam),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    staleTime: 30_000,
    enabled,
    retry: false,
  });
  return {
    query,
    incidents: uniqueById(query.data?.pages.flatMap((page) => page.incidents) || []),
    evaluatedAt: query.data?.pages[0]?.evaluatedAt,
  };
}

export function useUnifiedRules(service = unifiedAlertsService, enabled = true) {
  const query = useInfiniteQuery({
    queryKey: ['unified-alerts', 'rules'],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => service.rules(pageParam),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    staleTime: 30_000,
    enabled,
    retry: false,
  });
  return {
    query,
    rules: uniqueById(query.data?.pages.flatMap((page) => page.rules) || []),
    capabilities: query.data?.pages[0]?.capabilities || [],
  };
}

export function useIncidentHistory(incidentId: string, service = unifiedAlertsService) {
  const query = useInfiniteQuery({
    queryKey: ['unified-alerts', 'history', incidentId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => service.history(incidentId, pageParam),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    staleTime: 0,
    retry: false,
  });
  return {
    query,
    // The first page is the most recent snapshot; older timeline pages cannot replace it.
    incident: query.data?.pages[0]?.incident,
    timeline: uniqueById(query.data?.pages.flatMap((page) => page.timeline) || []),
  };
}

export function useAlertActions(service = unifiedAlertsService) {
  const client = useQueryClient();
  return {
    async saveRule(command: SaveRule) {
      const saved = await service.saveRule(command);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['unified-alerts'] }),
        client.invalidateQueries({ queryKey: ['unified-heartbeats'] }),
      ]);
      return saved;
    },
    async updateIncident(command: UpdateIncident) {
      const saved = await service.updateIncident(command);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['unified-alerts'] }),
        client.invalidateQueries({ queryKey: ['unified-heartbeats'] }),
      ]);
      return saved;
    },
  };
}
